import React from 'react';
import { useQuery } from '@apollo/client';
import { GET_RETURN_BILLING_PRICING_STATUS } from 'gql/queries';

export const RETURN_PRICING_ITEMS = [
  {
    outcome: 'ShipAid-rate return label',
    price: 'Carrier postage + $1.75',
    billingTrigger: 'Label is purchased',
  },
  {
    outcome: 'Merchant-connected carrier label',
    price: '$1.75 service fee',
    billingTrigger: 'Label is successfully created',
  },
  {
    outcome: 'Keep Your Item',
    price: '$0.75',
    billingTrigger: 'Resolution successfully completes',
  },
] as const;

export const LEGACY_RETURN_PRICING_TIERS = [
  { monthlyProcessedReturns: '0-500', price: '$1.00/return' },
  { monthlyProcessedReturns: '501-2,000', price: '$0.75/return' },
  { monthlyProcessedReturns: '2,001-5,000', price: '$0.50/return' },
  { monthlyProcessedReturns: '5,000+', price: 'Custom' },
] as const;

export type ReturnPricingMode = 'OFF' | 'SHADOW' | 'LIVE';

interface ReturnPricingTableProps {
  className?: string;
  mode: ReturnPricingMode;
  blockedReason?: string | null;
  activationPending?: boolean;
}

const LegacyPricingRows = () => (
  <>
    <thead className="bg-gray-50">
      <tr>
        <th scope="col" className="px-5 py-3 font-semibold text-gray-700">
          Monthly processed returns
        </th>
        <th scope="col" className="px-5 py-3 font-semibold text-gray-700">
          Price
        </th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-100 bg-white">
      {LEGACY_RETURN_PRICING_TIERS.map((tier) => (
        <tr key={tier.monthlyProcessedReturns}>
          <td className="whitespace-nowrap px-5 py-3 font-medium text-gray-900">
            {tier.monthlyProcessedReturns}
          </td>
          <td className="whitespace-nowrap px-5 py-3 font-semibold text-gray-900">
            {tier.price}
          </td>
        </tr>
      ))}
    </tbody>
  </>
);

const OutcomePricingRows = () => (
  <>
    <thead className="bg-gray-50">
      <tr>
        <th scope="col" className="px-5 py-3 font-semibold text-gray-700">
          Outcome
        </th>
        <th scope="col" className="px-5 py-3 font-semibold text-gray-700">
          Merchant billing
        </th>
        <th scope="col" className="px-5 py-3 font-semibold text-gray-700">
          Billed when
        </th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-100 bg-white">
      {RETURN_PRICING_ITEMS.map((item) => (
        <tr key={item.outcome}>
          <td className="px-5 py-3 font-medium text-gray-900">
            {item.outcome}
          </td>
          <td className="px-5 py-3 font-semibold text-gray-900">
            {item.price}
          </td>
          <td className="px-5 py-3 text-gray-600">{item.billingTrigger}</td>
        </tr>
      ))}
    </tbody>
  </>
);

export const ReturnPricingTable = ({
  className = '',
  mode,
  blockedReason = null,
  activationPending = false,
}: ReturnPricingTableProps) => {
  const legacy = mode === 'OFF';
  const shadow = mode === 'SHADOW';
  const pendingActivation = activationPending && !legacy;
  const billingBlocker =
    blockedReason === 'SHOPIFY_BILLING_REAUTHORIZATION_REQUIRED'
      ? {
          message:
            'Outcome billing is paused until updated Shopify billing terms are approved.',
          action: 'Review billing authorization',
        }
      : blockedReason === 'STRIPE_BILLING_CONNECTION_REQUIRED'
      ? {
          message:
            'Outcome billing is paused until Stripe billing is connected.',
          action: 'Connect billing',
        }
      : null;

  return (
    <section
      className={`overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm ${className}`}
    >
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-base font-semibold text-gray-900">
          {pendingActivation
            ? 'Outcome pricing after activation'
            : legacy
            ? 'Legacy returns pricing'
            : shadow
            ? 'Returns pricing preview'
            : 'Returns pricing'}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {pendingActivation
            ? 'These pricing v2 terms apply only after outcome billing is activated for this store. Until then, the current store billing version remains in effect.'
            : legacy
            ? 'Usage billing is based on monthly processed returns.'
            : shadow
            ? 'Preview only: no outcome charges are issued; pre-cutover returns retain their original pricing.'
            : 'No monthly fee. You are billed only for completed return outcomes.'}
        </p>
      </div>
      {billingBlocker && (
        <div className="border-b border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
          <p>{billingBlocker.message}</p>
          <a
            href="/settings/billing/subscription"
            className="mt-2 inline-block font-semibold text-amber-900 underline"
          >
            {billingBlocker.action}
          </a>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
          {legacy ? <LegacyPricingRows /> : <OutcomePricingRows />}
        </table>
      </div>
      {!legacy && (
        <div className="border-t border-gray-100 bg-blue-50 px-5 py-4">
          <p className="text-sm leading-relaxed text-blue-900">
            Customer deductions use only the carrier label cost. ShipAid service
            fees are billed separately to your business.
          </p>
        </div>
      )}
    </section>
  );
};

export type ReturnBillingPricingStatusQueryResult = {
  returnBillingPricingStatus?: {
    storeId?: string | null;
    pricingVersion?: number | null;
    rolloutMode?: string | null;
    effectiveAt?: string | null;
    blockedReason?: string | null;
  } | null;
};

export const StoreReturnPricingTable = ({
  storeId,
  className = '',
}: {
  storeId?: string | null;
  className?: string;
}) => {
  const { data, loading, error } =
    useQuery<ReturnBillingPricingStatusQueryResult>(
      GET_RETURN_BILLING_PRICING_STATUS,
      {
        variables: { storeId },
        skip: !storeId,
        fetchPolicy: 'network-only',
      },
    );
  const status = data?.returnBillingPricingStatus;

  if (!storeId || loading) {
    return (
      <section
        className={`rounded-lg border border-gray-200 bg-white px-5 py-6 text-sm text-gray-500 shadow-sm ${className}`}
      >
        Loading return pricing…
      </section>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <section
          role="alert"
          className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900 shadow-sm"
        >
          We couldn&apos;t confirm this store&apos;s active billing version.
          Current outcome pricing is shown below and applies only after Billing
          V2 is activated for this store.
        </section>
        <ReturnPricingTable className="mt-4" mode="LIVE" activationPending />
      </div>
    );
  }

  if (!status || status.storeId !== storeId) {
    return (
      <section
        role="alert"
        className={`rounded-lg border border-red-200 bg-red-50 px-5 py-6 text-sm font-medium text-red-700 shadow-sm ${className}`}
      >
        Return pricing couldn&apos;t be loaded. Refresh before changing billing.
      </section>
    );
  }

  const mode: ReturnPricingMode =
    Number(status.pricingVersion) === 2 &&
    (status.rolloutMode === 'SHADOW' || status.rolloutMode === 'LIVE')
      ? status.rolloutMode
      : 'OFF';

  return (
    <ReturnPricingTable
      className={className}
      mode={mode}
      blockedReason={status.blockedReason}
    />
  );
};

export default ReturnPricingTable;
